import { useEffect, useRef, useState, type FormEvent } from 'react';
import { create } from 'zustand';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { toast } from '@/stores/toastStore';

/**
 * TICKET-2026-004, C-03 / C-05 (31/10/2026) — fenêtres de l'application à la place des boîtes natives du navigateur
 * (`confirm`, `prompt`, `alert`). Les boîtes natives gèlent l'onglet, n'apparaissent pas toujours (navigateur qui les
 * bloque) et échouent en silence sous un automate : « Valider » ne faisait alors rien, sans message.
 *
 * Utilisables depuis n'importe quel gestionnaire, comme avant :
 *   if (!(await demander.confirmer({ titre: 'Valider la proforma ?', message: '…' }))) return;
 *   const motif = await demander.texte({ titre: 'Motif d'annulation', min: 5 }); if (motif === null) return;
 *   informer('Enregistré', 'La pièce a été validée.');
 */

interface DemandeConfirmation {
  type: 'confirmer';
  titre: string;
  message?: string;
  confirmer?: string;       // libellé du bouton (défaut « Confirmer »)
  danger?: boolean;         // geste irréversible : bouton rouge
  resoudre: (ok: boolean) => void;
}
interface DemandeTexte {
  type: 'texte';
  titre: string;
  message?: string;
  libelle?: string;
  defaut?: string;
  min?: number;             // longueur minimale (vérifiée AVANT l'envoi)
  multiligne?: boolean;
  confirmer?: string;
  danger?: boolean;
  resoudre: (valeur: string | null) => void;
}
type Demande = DemandeConfirmation | DemandeTexte;

const useFenetres = create<{ file: Demande[]; ajouter: (d: Demande) => void; retirer: () => void }>((set) => ({
  file: [],
  ajouter: (d) => set((s) => ({ file: [...s.file, d] })),
  retirer: () => set((s) => ({ file: s.file.slice(1) })),
}));

type SansResoudre<T> = Omit<T, 'type' | 'resoudre'>;

export const demander = {
  /** Vrai si l'utilisateur confirme, faux s'il annule ou ferme. */
  confirmer: (d: SansResoudre<DemandeConfirmation>) =>
    new Promise<boolean>((resoudre) => useFenetres.getState().ajouter({ ...d, type: 'confirmer', resoudre })),
  /** Le texte saisi (déjà débarrassé de ses espaces de bord), ou null si l'utilisateur annule. */
  texte: (d: SansResoudre<DemandeTexte>) =>
    new Promise<string | null>((resoudre) => useFenetres.getState().ajouter({ ...d, type: 'texte', resoudre })),
};

/** Message d'information, qui ne bloque rien (à la place d'`alert`). */
export const informer = (titre: string, message?: string) => toast.info(titre, message);

/** Monté une fois (AppLayout) : affiche la première demande de la file. */
export const FenetresHost = () => {
  const d = useFenetres((s) => s.file[0]);
  const retirer = useFenetres((s) => s.retirer);
  const [valeur, setValeur] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const champ = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!d) return;
    setValeur(d.type === 'texte' ? d.defaut ?? '' : '');
    setErreur(null);
    const t = setTimeout(() => champ.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [d]);

  if (!d) return null;

  const fermer = (resultat: boolean | string | null) => {
    retirer();
    if (d.type === 'confirmer') d.resoudre(resultat === true);
    else d.resoudre(typeof resultat === 'string' ? resultat : null);
  };

  const valider = (e: FormEvent) => {
    e.preventDefault();
    if (d.type === 'confirmer') return fermer(true);
    const v = valeur.trim();
    if (d.min && v.length < d.min) {
      setErreur(`Au moins ${d.min} caractères (${v.length} saisis).`);
      return;
    }
    fermer(v);
  };

  return (
    <Dialog open onClose={() => fermer(d.type === 'confirmer' ? false : null)} title={d.titre} size="sm">
      <form onSubmit={valider} className="space-y-3">
        {d.message && (
          <div className="flex gap-2 text-sm text-gray-700" style={{ whiteSpace: 'pre-line' }}>
            {d.danger && <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />}
            <span>{d.message}</span>
          </div>
        )}
        {d.type === 'texte' && (
          <label className="block space-y-1">
            {d.libelle && <span className="text-xs font-medium text-gray-700">{d.libelle}</span>}
            {d.multiligne ? (
              <textarea ref={champ} rows={3} value={valeur} onChange={(e) => { setValeur(e.target.value); setErreur(null); }}
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm" />
            ) : (
              <input ref={champ} value={valeur} onChange={(e) => { setValeur(e.target.value); setErreur(null); }}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" />
            )}
            {erreur && <span className="block text-xs text-red-700">{erreur}</span>}
          </label>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={() => fermer(d.type === 'confirmer' ? false : null)}>Annuler</Button>
          <Button type="submit" variant={d.danger ? 'danger' : 'primary'} autoFocus={d.type === 'confirmer'}>{d.confirmer ?? 'Confirmer'}</Button>
        </div>
      </form>
    </Dialog>
  );
};
